import TagManager from 'react-gtm-module';

// Maps a path segment to the placeholder name used for whatever ID follows it.
// Every one of these route segments is followed by an org/integration/content/
// podcast ID in the app's URL scheme (src/modules/ProtectedRoutes.js), which is
// why GA4 was recording ~18,800 distinct page addresses per 90 days instead of
// grouping equivalent pages together.
const ID_PARENT_SEGMENTS = {
  organisation: 'org_id',
  integration: 'integration_id',
  content: 'content_id',
  podcast: 'podcast_id',
};

// Segments that can legitimately follow an ID-parent segment without being an
// ID themselves (e.g. ".../content/new"). Left untouched so the normalized
// path still distinguishes "content editor" from "new content".
const RESERVED_NEXT_SEGMENTS = new Set(['new']);

// Replaces dynamic ID segments in the pathname with named placeholders and
// returns the extracted IDs, so equivalent pages across users collapse into
// one GA4 row instead of one row per org/integration/content combination.
export function normalizePathname(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const normalized = [];
  const ids = {};

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    normalized.push(segment);

    const idParamName = ID_PARENT_SEGMENTS[segment];
    const nextSegment = segments[i + 1];

    if (idParamName && nextSegment && !RESERVED_NEXT_SEGMENTS.has(nextSegment)) {
      normalized.push(`{${idParamName}}`);
      ids[idParamName] = nextSegment;
      i++;
    }
  }

  return { path: '/' + normalized.join('/'), ids };
}

// `state` (signin/signup routing flag) and `plan_id` are the only query params
// GA4 ever needs to know about, and neither is ever baked into page_location —
// /?state=signup&plan_id=free-plan was the single largest page in the property
// (20,863 views/90d) specifically because those params were left in the URL,
// splitting the homepage across rows. `state` is an internal routing flag with
// no analytics value, so it's parsed but dropped; only `plan_id` is forwarded,
// as its own event parameter.
function extractPlanId(search) {
  const withoutLeadingQuestionMark = search.replace(/^\?/, '');
  // A malformed search string repeats the querystring with a second literal
  // "?" instead of "&" as the separator. Parsing only up to the first "?"
  // recovers the intended params and drops the duplicate tail either way.
  const firstSegment = withoutLeadingQuestionMark.split('?')[0];
  const params = new URLSearchParams(firstSegment);

  const result = {};
  if (params.has('plan_id')) result.plan_id = params.get('plan_id');
  return result;
}

// Pushes a clean, normalized page_view to the GTM data layer. Call this on
// initial load and on every client-side route change.
export function trackPageView() {
  const { path, ids } = normalizePathname(window.location.pathname);
  const params = extractPlanId(window.location.search);

  const payload = {
    event: 'page_view',
    page_path: path,
    page_location: window.location.origin + path,
    ...ids,
    ...params,
  };

  TagManager.dataLayer({ dataLayer: payload });
}
